import { Button as ButtonPrimitive } from "@base-ui/react/button";
import { cva, type VariantProps } from "class-variance-authority";
import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "group/button inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 overflow-visible whitespace-nowrap rounded-lg border bg-clip-padding font-medium text-sm outline-none transition-colors select-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: "border-transparent bg-primary text-primary-foreground hover:bg-primary/90",
        accent: "border-transparent bg-accent text-accent-foreground hover:bg-accent-hover",
        outline:
          "border-border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground aria-expanded:bg-accent",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost:
          "border-transparent hover:bg-accent hover:text-accent-foreground aria-expanded:bg-accent",
        "outline-success":
          "border-success/40 bg-background text-success shadow-xs hover:bg-success-container hover:text-success-container-foreground focus-visible:ring-success/30",
        destructive:
          "border-transparent bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:bg-destructive/60 dark:focus-visible:ring-destructive/40",
        link: "h-auto border-transparent px-0 text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-8 px-4 has-data-[icon=inline-end]:pr-3 has-data-[icon=inline-start]:pl-3",
        xs: "h-7 gap-1 px-2 text-xs has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-8 gap-1.5 px-3 has-data-[icon=inline-end]:pr-2.5 has-data-[icon=inline-start]:pl-2.5 [&_svg:not([class*='size-'])]:size-3.5",
        lg: "h-10 px-6 has-data-[icon=inline-end]:pr-4 has-data-[icon=inline-start]:pl-4",
        icon: "size-8 px-0 [&_svg:not([class*='size-'])]:size-4",
        "icon-xs": "size-7 px-0 [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-8 px-0 [&_svg:not([class*='size-'])]:size-3.5",
        "icon-lg": "size-10 px-0 [&_svg:not([class*='size-'])]:size-4",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

function Button({
  className,
  variant = "default",
  size = "default",
  ...props
}: ButtonPrimitive.Props & VariantProps<typeof buttonVariants>) {
  return (
    <ButtonPrimitive
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

function UnavailableAction({
  children,
  className,
  reason,
  reasonPlacement = "caption",
  size = "default",
  variant = "default",
}: {
  children: ReactNode;
  className?: string;
  reason: string;
  reasonPlacement?: "caption" | "title";
} & VariantProps<typeof buttonVariants>) {
  return (
    <span
      className={cn(
        "inline-flex items-start gap-1.5",
        reasonPlacement === "caption" && "max-w-64 flex-col"
      )}
    >
      <Button
        aria-label={`Unavailable: ${reason}`}
        className={className}
        disabled
        size={size}
        title={reason}
        variant={variant}
      >
        {children}
      </Button>
      {reasonPlacement === "caption" ? (
        <span className="text-muted-foreground text-xs leading-4">Unavailable: {reason}</span>
      ) : null}
    </span>
  );
}

export { Button, buttonVariants, UnavailableAction };
